import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Collapse,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Badge,
  ImageList,
  ImageListItem,
  InputAdornment,
  Stack,
  TableSortLabel
} from '@mui/material';
import {
  ShoppingBag,
  LocalShipping,
  CheckCircle,
  Cancel,
  Payment,
  ExpandMore,
  ExpandLess,
  Visibility,
  Edit,
  Download,
  Refresh,
  Image as ImageIcon,
  LocationOn,
  Phone,
  Email,
  Build,
  LocalShippingOutlined,
  DoneAll,
  Assignment,
  Search,
  FilterList,
  ArrowUpward,
  ArrowDownward
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { orderService, adminService } from '../../services/api';
import { toast } from 'react-toastify';
import { COLORS } from '../../theme/colors';

const Orders = () => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [updateStatusDialog, setUpdateStatusDialog] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [imageDialog, setImageDialog] = useState({ open: false, images: [], orderId: null, itemIndex: null });
  const [highResImageDialog, setHighResImageDialog] = useState({ open: false, imageUrl: '', loading: false });

  const orderStatuses = [
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'confirmed', label: 'Confirmed', color: 'info' },
    { value: 'inProduction', label: 'In Production', color: 'primary' },
    { value: 'readyForPickup', label: 'Ready for Pickup', color: 'secondary' },
    { value: 'shipped', label: 'Shipped', color: 'default' },
    { value: 'delivered', label: 'Delivered', color: 'success' },
    { value: 'cancelled', label: 'Cancelled', color: 'error' }
  ];

  useEffect(() => {
    fetchOrders();
  }, []);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setPage(0); // Reset to first page when searching
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    // Refetch orders when filter changes
    setPage(0); // Reset to first page when filter changes
    fetchOrders();
  }, [statusFilter, page, rowsPerPage]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const response = await adminService.getAllOrders({ 
        page: page + 1, 
        limit: rowsPerPage,
        status: statusFilter 
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusInfo = orderStatuses.find(s => s.value === status) || orderStatuses[0];
    return (
      <Chip
        label={statusInfo.label}
        color={statusInfo.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const handleStatusUpdate = async () => {
    if (!updateStatusDialog || !newStatus) return;

    try {
      await adminService.updateOrderStatus(updateStatusDialog.id, newStatus);
      const orderIdentifier = updateStatusDialog.orderNumber || updateStatusDialog.id;
      toast.success(`Order ${orderIdentifier} status updated successfully`);
  // Optimistically update local state instead of refetching whole list
  setOrders(prev => prev.map(o => o.id === updateStatusDialog.id ? { ...o, status: newStatus, updatedAt: new Date().toISOString() } : o));
  setUpdateStatusDialog(null);
  setNewStatus('');
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Failed to update order status');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('asc');
    }
    setPage(0); // Reset to first page when sorting
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />;
  };

  const handleImageDownload = async (orderId, itemIndex, imageIndex, fileName) => {
    try {
      const response = await adminService.downloadSingleImage(orderId, itemIndex, imageIndex);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'image/jpeg' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `order-${orderId}-item-${itemIndex + 1}-image-${imageIndex + 1}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up blob URL
      window.URL.revokeObjectURL(url);
      
      toast.success('Image downloaded successfully');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    }
  };

  // For direct URL downloads (used in dialogs)
  const handleDirectImageDownload = async (imageUrl, fileName) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'customer-image.jpg';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Image downloaded successfully');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    }
  };

  const handleDownloadAllImages = async (order) => {
    try {
      const response = await adminService.downloadOrderImages(order.id);
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `order-${order.orderNumber || order.id}-images.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Images downloaded successfully');
    } catch (error) {
      console.error('Error downloading all images:', error);
      toast.error('Failed to download images');
    }
  };

  const handleShowHighResImage = async (orderId, itemIndex, imageIndex) => {
    try {
      setHighResImageDialog({ open: true, imageUrl: '', loading: true });
      const response = await adminService.getHighResImage(orderId, itemIndex, imageIndex);
      setHighResImageDialog({ 
        open: true, 
        imageUrl: response.data.highResUrl, 
        fileName: response.data.fileName,
        loading: false 
      });
    } catch (error) {
      console.error('Error loading high-res image:', error);
      toast.error('Failed to load high-res image');
      setHighResImageDialog({ open: false, imageUrl: '', loading: false });
    }
  };

  const filteredOrders = orders.filter(order => {
    // Status filter
    if (statusFilter !== 'all' && order.status !== statusFilter) {
      return false;
    }
    
    // Payment filter
    if (paymentFilter !== 'all' && order.paymentStatus !== paymentFilter) {
      return false;
    }
    
    // Date filter
    if (dateFilter !== 'all') {
      const orderDateRaw = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
      const orderDate = isNaN(orderDateRaw?.getTime()) && typeof order.createdAt === 'string' ? new Date(order.createdAt) : orderDateRaw;
      const now = new Date();
      const daysDiff = Math.floor((now - orderDate) / (1000 * 60 * 60 * 24));
      
      switch (dateFilter) {
        case 'today':
          if (daysDiff !== 0) return false;
          break;
        case 'week':
          if (daysDiff > 7) return false;
          break;
        case 'month':
          if (daysDiff > 30) return false;
          break;
        default:
          break;
      }
    }
    
    // Search query filter
    if (debouncedSearchQuery.trim()) {
      const query = debouncedSearchQuery.toLowerCase().trim();
      const searchableFields = [
        order.orderNumber,
        order.id,
        order.transactionId,
        // Primary stored user name/email on order (covers COD & legacy orders)
        order.userName,
        order.userEmail,
        // Payment gateway response
        order.paymentResponse?.firstname,
        order.paymentResponse?.email,
        // Shipping address fallbacks
        order.shippingAddress?.name,
        order.shippingAddress?.email,
        order.shippingAddress?.phone,
        // Product names
        order.items?.map(item => item.name).join(' ')
      ].filter(Boolean).join(' ').toLowerCase();
      
      if (!searchableFields.includes(query)) {
        return false;
      }
    }
    
    return true;
  });

  // Sort orders
  const sortedOrders = [...filteredOrders].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'orderNumber':
        aValue = a.orderNumber || a.id;
        bValue = b.orderNumber || b.id;
        break;
      case 'customer':
        aValue = (a.userName || a.paymentResponse?.firstname || a.shippingAddress?.name || '').toLowerCase();
        bValue = (b.userName || b.paymentResponse?.firstname || b.shippingAddress?.name || '').toLowerCase();
        break;
      case 'total':
        aValue = a.pricing?.total || 0;
        bValue = b.pricing?.total || 0;
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'createdAt':
      default:
  const aDateRaw = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
  const bDateRaw = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
  aValue = isNaN(aDateRaw?.getTime()) && typeof a.createdAt === 'string' ? Date.parse(a.createdAt) : aDateRaw;
  bValue = isNaN(bDateRaw?.getTime()) && typeof b.createdAt === 'string' ? Date.parse(b.createdAt) : bDateRaw;
        break;
    }
    
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedOrders = sortedOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    try {
      let date;
      if (value?.toDate && typeof value.toDate === 'function') {
        date = value.toDate();
      } else if (value && typeof value === 'object' && 'seconds' in value && typeof value.seconds === 'number') {
        date = new Date(value.seconds * 1000);
      } else if (typeof value === 'number') {
        date = value < 1e12 ? new Date(value * 1000) : new Date(value);
      } else if (typeof value === 'string') {
        const firestoreHumanPattern = /at\s/;
        if (firestoreHumanPattern.test(value) && !/T\d{2}:\d{2}/.test(value)) {
          const cleaned = value.replace(' at', '').replace('UTC', 'GMT');
          date = new Date(cleaned);
        } else {
          date = new Date(value);
        }
      } else if (value instanceof Date) {
        date = value;
      }
      if (!date || isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    } catch (e) {
      console.error('Error formatting date:', e, value);
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading orders...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Admin Orders Management
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          Manage all customer orders, update statuses, and download uploaded images.
        </Typography>
        
        <Box sx={{ display: 'flex', flex: 1, gap: 2, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
          {/* Search Field */}
          <TextField
            size="small"
            placeholder="Search by order number, customer name, email, or transaction ID..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            sx={{ minWidth: 300, flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {searchQuery && searchQuery !== debouncedSearchQuery ? (
                    <CircularProgress size={16} />
                  ) : (
                    <Search color="action" />
                  )}
                </InputAdornment>
              ),
            }}
          />
          
          {/* Filters */}
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Status</MenuItem>
                {orderStatuses.map(status => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Payment</InputLabel>
              <Select
                value={paymentFilter}
                label="Payment"
                onChange={(e) => {
                  setPaymentFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Payments</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Date</InputLabel>
              <Select
                value={dateFilter}
                label="Date"
                onChange={(e) => {
                  setDateFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="createdAt">Date Created</MenuItem>
                <MenuItem value="orderNumber">Order Number</MenuItem>
                <MenuItem value="customer">Customer Name</MenuItem>
                <MenuItem value="total">Total Amount</MenuItem>
                <MenuItem value="status">Status</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              startIcon={sortDirection === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
              sx={{ minWidth: 40 }}
            >
              {sortDirection.toUpperCase()}
            </Button>
          </Stack>
          
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchOrders}
          >
            Refresh
          </Button>
        </Box>
        
        {/* Results Summary and Clear Filters */}
        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {sortedOrders.length} of {orders.length} orders
            {(debouncedSearchQuery || statusFilter !== 'all' || paymentFilter !== 'all' || dateFilter !== 'all') && (
              <> (filtered)</>
            )}
            {` • Sorted by ${sortBy === 'createdAt' ? 'Date Created' : 
                               sortBy === 'orderNumber' ? 'Order Number' : 
                               sortBy === 'customer' ? 'Customer Name' : 
                               sortBy === 'total' ? 'Total Amount' : 
                               'Status'} (${sortDirection.toUpperCase()})`}
          </Typography>
          
          {(debouncedSearchQuery || statusFilter !== 'all' || paymentFilter !== 'all' || dateFilter !== 'all' || sortBy !== 'createdAt' || sortDirection !== 'desc') && (
            <Button
              size="small"
              onClick={() => {
                setSearchQuery('');
                setDebouncedSearchQuery('');
                setStatusFilter('all');
                setPaymentFilter('all');
                setDateFilter('all');
                setSortBy('createdAt');
                setSortDirection('desc');
                setPage(0);
              }}
              startIcon={<FilterList />}
            >
              Clear All Filters
            </Button>
          )}
        </Box>
      </Box>

      {sortedOrders.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Assignment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No orders found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {statusFilter === 'all' 
              ? 'No orders have been placed yet.' 
              : `No orders with status "${orderStatuses.find(s => s.value === statusFilter)?.label}" found.`
            }
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'orderNumber'}
                      direction={sortBy === 'orderNumber' ? sortDirection : 'asc'}
                      onClick={() => handleSort('orderNumber')}
                    >
                      Order Number
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'customer'}
                      direction={sortBy === 'customer' ? sortDirection : 'asc'}
                      onClick={() => handleSort('customer')}
                    >
                      Customer
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'total'}
                      direction={sortBy === 'total' ? sortDirection : 'asc'}
                      onClick={() => handleSort('total')}
                    >
                      Total
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'status'}
                      direction={sortBy === 'status' ? sortDirection : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Payment</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'createdAt'}
                      direction={sortBy === 'createdAt' ? sortDirection : 'asc'}
                      onClick={() => handleSort('createdAt')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOrders.map((order) => (
                  <React.Fragment key={order.id}>
                    <TableRow hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {order.orderNumber || order.id}
                        </Typography>
                        {order.orderNumber && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            ID: {order.id}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {order.userName || order.paymentResponse?.firstname || order.shippingAddress?.name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.userEmail || order.paymentResponse?.email || order.shippingAddress?.email || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {order.items?.length || 0} item(s)
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPrice(order.pricing?.total || 0)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {getStatusChip(order.status)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.paymentStatus || 'Unknown'}
                          size="small"
                          color={order.paymentStatus === 'paid' ? 'success' : 'warning'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {formatDate(order.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setExpandedOrder(
                                expandedOrder === order.id ? null : order.id
                              )}
                            >
                              {expandedOrder === order.id ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Update Status">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setUpdateStatusDialog(order);
                                setNewStatus(order.status);
                              }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded Order Details */}
                    <TableRow>
                      <TableCell colSpan={8} sx={{ p: 0, borderBottom: 'none' }}>
                        <Collapse in={expandedOrder === order.id}>
                          <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                            {/* Order Header */}
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                              <Typography variant="h6" fontWeight="bold" gutterBottom>
                                {order.orderNumber || `Order ${order.id}`}
                              </Typography>
                              {order.orderNumber && (
                                <Typography variant="caption" color="text.secondary">
                                  Document ID: {order.id}
                                </Typography>
                              )}
                              {order.transactionId && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  Transaction ID: {order.transactionId}
                                </Typography>
                              )}
                              {order.orderNotes && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>Notes:</strong> {order.orderNotes}
                                </Typography>
                              )}
                            </Box>
                            
                            <Grid container spacing={3}>
                              {/* Order Items */}
                              <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                  Order Items
                                </Typography>
                                {order.items?.map((item, index) => (
                                  <Card key={index} sx={{ mb: 2 }}>
                                    <CardContent sx={{ p: 2 }}>
                                      <Grid container spacing={2} alignItems="center">
                                        {item.image && (
                                          <Grid item xs={3}>
                                            <CardMedia
                                              component="img"
                                              image={item.image}
                                              alt={item.name}
                                              sx={{ 
                                                height: 60, 
                                                width: 60, 
                                                objectFit: 'cover',
                                                borderRadius: 1
                                              }}
                                            />
                                          </Grid>
                                        )}
                                        <Grid item xs={item.image ? 9 : 12}>
                                          <Typography variant="body2" fontWeight="medium">
                                            {item.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            Qty: {item.quantity} × {formatPrice(item.price)}
                                          </Typography>
                                          {item.customizations && (
                                            <Box sx={{ mt: 0.5 }}>
                                              {item.customizations.text && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                  Custom Text: "{item.customizations.text}"
                                                </Typography>
                                              )}
                                              {item.customizations.images && item.customizations.images.length > 0 && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                  Custom Images: {item.customizations.images.length} uploaded
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                        </Grid>
                                      </Grid>
                                      
                                      {/* Customer Images and Customizations */}
                                      {(item.customizations?.images?.length > 0 || item.customizations?.text) && (
                                        <Box sx={{ mt: 2 }}>
                                          {/* Text Customization */}
                                          {item.customizations?.text && (
                                            <Box sx={{ mb: 2 }}>
                                              <Typography variant="caption" fontWeight="medium" gutterBottom>
                                                Text Customization:
                                              </Typography>
                                              <Typography variant="body2" sx={{ 
                                                p: 1, 
                                                bgcolor: 'grey.100', 
                                                borderRadius: 1,
                                                fontStyle: 'italic'
                                              }}>
                                                "{item.customizations.text}"
                                              </Typography>
                                            </Box>
                                          )}

                                          {/* Customer Images */}
                                          {item.customizations?.images?.length > 0 && (
                                            <Box>
                                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                                <Typography variant="caption" fontWeight="medium">
                                                  Customer Images ({item.customizations.images.length}):
                                                </Typography>
                                                {/* Always show Download All button for customer images */}
                                                <Button
                                                  size="small"
                                                  variant="outlined"
                                                  startIcon={<Download />}
                                                  onClick={() => {
                                                    handleDownloadAllImages(order);
                                                  }}
                                                  sx={{ fontSize: '0.75rem', py: 0.5, px: 1 }}
                                                >
                                                  Download All
                                                </Button>
                                              </Box>
                                              
                                              <ImageList cols={4} gap={8} sx={{ height: 180, mt: 1 }}>
                                                {item.customizations.images.map((imageUrl, imgIndex) => {
                                                  // Check if we have processed images for this order
                                                  const processedItem = order.processedImages?.find(pi => pi.itemIndex === index);
                                                  const processedImage = processedItem?.images?.[imgIndex];
                                                  
                                                  // Use low-res version if available, otherwise use original
                                                  const displayImage = processedImage?.lowResUrl || imageUrl;
                                                  const isProcessed = !!processedImage;

                                                  return (
                                                    <ImageListItem key={imgIndex} sx={{ position: 'relative' }}>
                                                      <img
                                                        src={displayImage}
                                                        alt={`Customer upload ${imgIndex + 1}`}
                                                        style={{ 
                                                          height: '140px', 
                                                          width: '100%', 
                                                          objectFit: 'cover',
                                                          cursor: 'pointer',
                                                          borderRadius: '4px',
                                                          border: isProcessed ? `2px solid ${COLORS.primary.main}` : '1px solid #ddd'
                                                        }}
                                                        onClick={() => {
                                                          if (isProcessed) {
                                                            handleShowHighResImage(order.id, index, imgIndex);
                                                          } else {
                                                            setImageDialog({ 
                                                              open: true, 
                                                              images: [imageUrl],
                                                              orderId: order.id,
                                                              itemIndex: index
                                                            });
                                                          }
                                                        }}
                                                      />
                                                      
                                                      {/* Status indicator */}
                                                      {isProcessed && (
                                                        <Chip
                                                          label="Processed"
                                                          size="small"
                                                          color="success"
                                                          sx={{
                                                            position: 'absolute',
                                                            top: 4,
                                                            left: 4,
                                                            fontSize: '10px',
                                                            height: '20px'
                                                          }}
                                                        />
                                                      )}
                                                      
                                                      {/* Download button */}
                                                      <Box
                                                        sx={{
                                                          position: 'absolute',
                                                          top: 4,
                                                          right: 4,
                                                          bgcolor: 'rgba(0,0,0,0.7)',
                                                          borderRadius: '50%',
                                                          p: 0.5
                                                        }}
                                                      >
                                                        <IconButton
                                                          size="small"
                                                          sx={{ color: 'white', p: 0.5 }}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleImageDownload(
                                                              order.id,
                                                              index,
                                                              imgIndex,
                                                              `order-${order.orderNumber || order.id}-item-${index + 1}-image-${imgIndex + 1}.jpg`
                                                            );
                                                          }}
                                                        >
                                                          <Download sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                      </Box>
                                                      
                                                      {/* Image info */}
                                                      <Box sx={{
                                                        position: 'absolute',
                                                        bottom: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bgcolor: 'rgba(0,0,0,0.7)',
                                                        color: 'white',
                                                        p: 0.5,
                                                        fontSize: '10px',
                                                        textAlign: 'center'
                                                      }}>
                                                        {isProcessed ? 'Low-Res Preview' : 'Original'}
                                                      </Box>
                                                    </ImageListItem>
                                                  );
                                                })}
                                              </ImageList>
                                            </Box>
                                          )}
                                        </Box>
                                      )}
                                    </CardContent>
                                  </Card>
                                ))}
                              </Grid>
                              
                              {/* Customer Information */}
                              <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                  Customer Information
                                </Typography>
                                <Paper sx={{ p: 2 }}>
                                  <List dense>
                                    <ListItem>
                                      <ListItemText
                                        primary="Name"
                                        secondary={order.userName || order.paymentResponse?.firstname || order.shippingAddress?.name || 'N/A'}
                                      />
                                    </ListItem>
                                    <ListItem>
                                      <ListItemText
                                        primary="Email"
                                        secondary={order.userEmail || order.paymentResponse?.email || order.shippingAddress?.email || 'N/A'}
                                      />
                                    </ListItem>
                                    <ListItem>
                                      <ListItemText
                                        primary="Phone"
                                        secondary={order.paymentResponse?.phone || order.shippingAddress?.phoneNumber || 'N/A'}
                                      />
                                    </ListItem>
                                    <ListItem>
                                      <ListItemText
                                        primary="Address"
                                        secondary={
                                          order.shippingAddress?.fullAddress || 
                                          (order.shippingAddress 
                                            ? `${order.shippingAddress.addressLine1}, ${order.shippingAddress.city}, ${order.shippingAddress.state} - ${order.shippingAddress.pincode}`
                                            : 'N/A')
                                        }
                                      />
                                    </ListItem>
                                    {order.orderNotes && (
                                      <ListItem>
                                        <ListItemText
                                          primary="Order Notes"
                                          secondary={order.orderNotes}
                                        />
                                      </ListItem>
                                    )}
                                    {order.paymentResponse && (
                                      <>
                                        <ListItem>
                                          <ListItemText
                                            primary="Payment Method"
                                            secondary={`${order.paymentResponse.mode} (${order.paymentResponse.bankcode || 'N/A'})`}
                                          />
                                        </ListItem>
                                        {order.paymentResponse.card_no && (
                                          <ListItem>
                                            <ListItemText
                                              primary="Card Number"
                                              secondary={order.paymentResponse.card_no}
                                            />
                                          </ListItem>
                                        )}
                                      </>
                                    )}
                                    {order.pricing && (
                                      <>
                                        <ListItem>
                                          <ListItemText
                                            primary="Order Total"
                                            secondary={
                                              <Box>
                                                <Typography variant="body2">
                                                  Subtotal: {formatPrice(order.pricing.subtotal)}
                                                </Typography>
                                                <Typography variant="body2">
                                                  Shipping: {formatPrice(order.pricing.shipping)}
                                                </Typography>
                                                <Typography variant="body2">
                                                  Tax: {formatPrice(order.pricing.tax)}
                                                </Typography>
                                                {order.pricing.discount > 0 && (
                                                  <Typography variant="body2" color="success.main">
                                                    Discount: -{formatPrice(order.pricing.discount)}
                                                  </Typography>
                                                )}
                                                <Typography variant="h6" color="primary.main">
                                                  Total: {formatPrice(order.pricing.total)}
                                                </Typography>
                                              </Box>
                                            }
                                          />
                                        </ListItem>
                                      </>
                                    )}
                                    <ListItem>
                                      <ListItemText
                                        primary="Payment Method"
                                        secondary={order.paymentResponse?.mode || order.paymentMethod || 'N/A'}
                                      />
                                    </ListItem>
                                    {order.paymentResponse?.bank_ref_no && (
                                      <ListItem>
                                        <ListItemText
                                          primary="Bank Reference"
                                          secondary={order.paymentResponse.bank_ref_no}
                                        />
                                      </ListItem>
                                    )}
                                  </List>
                                </Paper>
                              </Grid>
                            </Grid>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={sortedOrders.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            labelRowsPerPage="Orders per page:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}–${to} of ${count !== -1 ? count : `more than ${to}`} orders`
            }
            showFirstButton
            showLastButton
          />
        </>
      )}

      {/* Status Update Dialog */}
      <Dialog open={Boolean(updateStatusDialog)} onClose={() => setUpdateStatusDialog(null)}>
        <DialogTitle>Update Order Status</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Order: {updateStatusDialog?.orderNumber || updateStatusDialog?.id}
          </Typography>
          {updateStatusDialog?.orderNumber && (
            <Typography variant="caption" color="text.secondary" gutterBottom>
              Document ID: {updateStatusDialog?.id}
            </Typography>
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {orderStatuses.map(status => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUpdateStatusDialog(null)}>Cancel</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog 
        open={imageDialog.open} 
        onClose={() => setImageDialog({ open: false, images: [] })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Customer Images</DialogTitle>
        <DialogContent>
          <ImageList cols={2} gap={8}>
            {imageDialog.images.map((imageUrl, index) => (
              <ImageListItem key={index}>
                <img
                  src={imageUrl}
                  alt={`Customer upload ${index + 1}`}
                  style={{ height: '300px', objectFit: 'contain' }}
                />
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <Button
                    size="small"
                    startIcon={<Download />}
                    onClick={() => handleDirectImageDownload(imageUrl, `customer-image-${index + 1}.jpg`)}
                  >
                    Download
                  </Button>
                </Box>
              </ImageListItem>
            ))}
          </ImageList>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialog({ open: false, images: [] })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* High-Res Image Viewer Dialog */}
      <Dialog 
        open={highResImageDialog.open} 
        onClose={() => setHighResImageDialog({ open: false, imageUrl: '', loading: false })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          High Resolution Image
          {highResImageDialog.fileName && (
            <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
              {highResImageDialog.fileName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', minHeight: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {highResImageDialog.loading ? (
            <CircularProgress />
          ) : (
            <img
              src={highResImageDialog.imageUrl}
              alt="High resolution customer upload"
              style={{ 
                maxWidth: '100%', 
                maxHeight: '70vh', 
                objectFit: 'contain',
                borderRadius: '8px'
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHighResImageDialog({ open: false, imageUrl: '', loading: false })}>
            Close
          </Button>
          {!highResImageDialog.loading && highResImageDialog.imageUrl && (
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => handleDirectImageDownload(
                highResImageDialog.imageUrl, 
                `${highResImageDialog.fileName}-high.jpg`
              )}
            >
              Download High-Res
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Orders;
